import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const categories = ['Industrial Gases', 'Industrial Mixtures', 'Medical Gases', 'Cryogenic Liquids', 'Gas Accessories'];

const products = {
  'Industrial Gases': {
    desc: 'High-purity industrial gases manufactured to meet the stringent requirements of various industrial applications including welding, cutting, and manufacturing processes.',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/poxeibc1_image.png',
    items: [
      { name: 'Oxygen (O₂)', use: 'Cutting, welding, breathing equipment, medical applications' },
      { name: 'Nitrogen (N₂)', use: 'Blanketing, purging, food preservation, laser cutting' },
      { name: 'Argon (Ar)', use: 'TIG/MIG welding shielding, metallurgy, electronics manufacturing' },
      { name: 'Carbon Dioxide (CO₂)', use: 'MIG welding, food packaging, refrigeration, fire suppression' },
      { name: 'Helium (He)', use: 'Leak detection, balloon inflation, MRI cooling, welding' },
      { name: 'Hydrogen (H₂)', use: 'Fuel cells, heat treatment, chemical synthesis, semiconductor manufacturing' },
      { name: 'Propane (C₃H₈)', use: 'Cutting, brazing, heating, industrial fuel and flame hardening applications' },
      { name: 'Acetylene (C₂H₂)', use: 'Oxy-acetylene welding and cutting, highest flame temperature of any combustible gas' },
      { name: 'Dimethyl Ether (DME)', use: 'Alternative fuel for LPG replacement, aerosol propellant, chemical synthesis, and clean energy applications' },
    ],
  },
  'Industrial Mixtures': {
    desc: 'Precisely formulated gas mixtures for enhanced industrial performance. All mixtures are available in custom ratios based on customer specifications.',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/1fx5qxof_image_be3ebf2b.jpeg',
    items: [
      { name: 'Argon + Carbon Dioxide', use: 'Shielding gas for Carbon Steel MIG welding' },
      { name: 'Argon + Oxygen', use: 'Shielding gas for Stainless Steel MIG welding' },
      { name: 'Argon + Hydrogen', use: 'Shielding gas for Austenitic Stainless Steel' },
      { name: 'Argon + Nitrogen', use: 'Auto bulbs and specialty lighting applications' },
      { name: 'Argon + Helium', use: 'Shielding gas for Aluminum TIG welding' },
      { name: 'Nitrogen + Hydrogen', use: 'Galvanization and heat treatment processes' },
    ],
  },
  'Medical Gases': {
    desc: 'Medical gases manufactured exclusively to meet the demands of the medical sector using best-in-class technology and safety solutions, with color-coded cylinders for hospitals.',
    img: 'https://images.pexels.com/photos/3845129/pexels-photo-3845129.jpeg?auto=compress&cs=tinysrgb&w=800',
    items: [
      { name: 'Medical Oxygen', use: 'Respiratory therapy, surgical procedures, critical care' },
      { name: 'Breathing Air', use: 'Ventilators, anesthesia machines, medical grade compressed air' },
      { name: 'Carbon Dioxide USP', use: 'Laparoscopic surgery, insufflation, endoscopy' },
      { name: 'Nitrous Oxide USP', use: 'General anesthesia, pain management, dental procedures' },
      { name: 'CO₂ in Oxygen', use: 'Medical Drug Therapy, blood gas calibration' },
      { name: 'Helium + Oxygen', use: 'Diving Mixture, medical drug therapy, lung diffusion testing' },
    ],
  },
  'Cryogenic Liquids': {
    desc: 'Cryogenic liquids are gases maintained in liquid form at very low temperatures. A small amount expands into large volumes of gas, ideal for bulk supply to high-volume users.',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/g3fobm89_image.png',
    items: [
      { name: 'Liquid Oxygen (LOX)', use: 'Medical oxygen systems, steel making, wastewater treatment' },
      { name: 'Liquid Nitrogen (LIN)', use: 'Cryopreservation, food freezing, shrink fitting, cooling' },
      { name: 'Liquid Argon (LAR)', use: 'High-purity argon production, specialty manufacturing' },
      { name: 'Liquid Carbon Dioxide (LCO₂)', use: 'Fire suppression, beverage carbonation, food processing' },
    ],
  },
  'Gas Accessories': {
    desc: 'A complete range of gas engineering products and accessories manufactured to the highest quality standards, ensuring safe and efficient gas delivery systems.',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/zmgw4lyj_image_52ef3d4a.jpeg',
    items: [
      { name: 'Industrial Gas Valves', use: 'Pressure regulation and control for cylinder applications' },
      { name: 'Cryogenic Valves', use: 'Low-temperature service valves for cryogenic vessels and pipelines' },
      { name: 'Gas Filling Systems', use: 'Complete cylinder filling and manifold systems' },
      { name: 'Cylinder Cascade Pallets', use: 'Multi-cylinder storage and delivery solutions' },
      { name: 'Pressure Regulators', use: 'Single and dual-stage regulators for all gas types' },
      { name: 'Safety Relief Devices', use: 'Burst discs, safety relief valves, and pressure sensing equipment' },
    ],
  },
};

export default function ProductsPage() {
  const [active, setActive] = useState('Industrial Gases');
  const current = products[active];

  return (
    <div className="pt-20">
      {/* HERO */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-red mb-4 block">Our Products</span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tighter font-heading mb-6">
              Gases, Liquids & Engineering Products
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              A comprehensive portfolio of industrial, medical, and specialty gases all manufactured using quality materials to meet the exacting requirements of our customers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CATEGORY TABS */}
      <section className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-0 hide-scrollbar">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`tab-${cat.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex-shrink-0 px-6 py-5 text-sm font-semibold border-b-2 transition-colors duration-200 ${active === cat ? 'border-brand-red text-brand-red' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT CONTENT */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <span className="section-tag mb-3 block">{active}</span>
                <h2 className="text-4xl font-bold text-gray-900 tracking-tight font-heading mb-5">{active}</h2>
                <p className="text-gray-600 leading-relaxed mb-10">{current.desc}</p>
                <div className="space-y-4">
                  {current.items.map((item, i) => (
                    <div key={item.name} className="border border-gray-200 p-5 hover:border-brand-red transition-colors duration-200" data-testid={`product-${i}`}>
                      <div className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-brand-red mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900 font-heading mb-1">{item.name}</h4>
                          <p className="text-gray-500 text-sm">{item.use}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-10">
                  <Link to="/contact" data-testid="products-enquire-btn" className="btn-primary">Enquire About {active}</Link>
                </div>
              </div>
              <div className="lg:sticky lg:top-40">
                <div className="relative overflow-hidden">
                  <img src={current.img} alt={active} className="w-full h-[480px] object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="text-white font-bold font-heading text-xl mb-1">{active}</p>
                    <p className="text-gray-300 text-sm">Premium quality, certified purity</p>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-6 mt-0 border-t-0">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Need a custom specification?</p>
                  <p className="text-xs text-gray-500 mb-4">Our technical team can formulate products to your exact requirements.</p>
                  <Link to="/contact" className="text-brand-red text-sm font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                    Talk to an Expert <CheckCircle size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-red mb-4 block">Our Serving Industries</span>
            <h2 className="text-3xl font-bold text-white font-heading mb-10">Powering Multiple Sectors</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {['Medical', 'Food & Beverage', 'Automobile', 'Manufacturing', 'Oil & Gas', 'Research & Lab', 'Aerospace', 'Construction'].map(ind => (
                <span key={ind} className="border border-gray-700 text-gray-300 px-5 py-2.5 text-sm hover:border-brand-red hover:text-brand-red transition-colors">{ind}</span>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/contact" className="btn-outline-white">Request a Quote</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
